import React, { useState } from 'react';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';
import { fakeSignerName } from 'src/devtools/fillers/w2';

import { Checkbox } from '@components/ui/checkbox';
import { FloatingInput } from '@components/ui/input-floating';

import { AGREEMENT_COPY } from '../constants/copy';
import { W2_SELECT_TID } from '../constants/testids';
import { formatDisplayDate, todayISO } from '../format';
import { W2SelectionStepLayout } from './W2SelectionStepLayout';

const TID = W2_SELECT_TID.agreement;

interface AgreementSignatureProps {
  signing: boolean;
  signError: string | null;
  onBack: () => void;
  /** Fires with the typed legal name once the gates pass. */
  onFinish: (signerName: string) => void;
  /** Bar position (STEP n / m). */
  step: number;
  total: number;
}

/**
 * Agreement / e-signature (client copy, screen 10), hosted in the shell: the
 * "Agreement" intro sits in the left maroon panel; the 7-clause TERMS + the
 * typed-name + attestation → FINISH-gated sign block stay in the middle card
 * (rearranged only — copy unchanged). FINISH lives IN the card (the panel shows
 * no NEXT), so the attestation/typed-name gating UX is untouched. FINISH is
 * active only after the attestation checkbox is checked AND a full name is typed;
 * signing date/time is recorded server-side on `POST /employee/signature`.
 */
export const AgreementSignature: React.FC<AgreementSignatureProps> = ({
  signing,
  signError,
  onBack,
  onFinish,
  step,
  total,
}) => {
  const [signerName, setSignerName] = useState('');
  const [authorized, setAuthorized] = useState(false);

  const signDate = formatDisplayDate(todayISO());
  const canFinish = authorized && signerName.trim() !== '' && !signing;

  return (
    <>
      {DEVFILL_ENABLED && (
        <DevFillButton
          testid={DEVFILL_TID.agreement}
          label="Fill signature"
          onFill={async () => {
            setSignerName(await fakeSignerName());
            setAuthorized(true);
          }}
        />
      )}
      <W2SelectionStepLayout
        step={step}
        total={total}
        title={AGREEMENT_COPY.headline}
        body={<p>{AGREEMENT_COPY.intro}</p>}
        rootTestId={TID.root}
      >
        <div className="flex flex-col gap-8 max-sm:gap-6">
          <section>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8a8aa0]">
              Terms
            </h2>
            <ol
              data-testid={TID.terms}
              className="max-h-96 list-decimal space-y-3 overflow-y-auto rounded-xl border border-[#ececf2] bg-[#fafafb] p-5 pl-8 max-sm:p-4 max-sm:pl-7"
            >
              {AGREEMENT_COPY.clauses.map((clause, i) => (
                <li key={i} className="text-[14px] leading-6 text-[#3a3a4e]">
                  {clause}
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-xl border border-[#ececf2] p-5 max-sm:p-4">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8a8aa0]">
              Sign to finish
            </h2>
            <div className="flex flex-col gap-4">
              <FloatingInput
                id="selection-signer-name"
                label={AGREEMENT_COPY.fieldTitle}
                type="text"
                autoComplete="name"
                value={signerName}
                onChange={(e) =>
                  setSignerName(e.target.value.replace(/^\s+/, ''))
                }
                data-testid={TID.signerName}
                inputClass="h-13.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 max-md:text-[18px]"
              />
              <p
                data-testid={TID.signDate}
                className="text-[14px] text-[#5a5a70]"
              >
                Date:{' '}
                <span className="font-semibold text-[#1a1a2e]">{signDate}</span>
              </p>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="selection-attestation"
                  data-testid={TID.attestation}
                  checked={authorized}
                  onCheckedChange={(checked) => setAuthorized(checked === true)}
                  className="mt-0.5 size-6 shrink-0 xl:size-6"
                />
                <label
                  htmlFor="selection-attestation"
                  className="cursor-pointer text-[15px] leading-6 text-[#1a1a2e]"
                >
                  {AGREEMENT_COPY.footer}
                </label>
              </div>

              {signError && (
                <p
                  data-testid={TID.signError}
                  role="alert"
                  className="text-[14px] leading-5 text-[#c0392b]"
                >
                  {signError}
                </p>
              )}

              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  data-testid={TID.back}
                  onClick={onBack}
                  className="rounded-full border border-[#c7c7d2] px-6 py-3 text-[15px] font-semibold text-[#5a5a70] hover:bg-[#f2f2f7]"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-testid={TID.finish}
                  disabled={!canFinish}
                  onClick={() => onFinish(signerName.trim())}
                  className="rounded-full bg-primary px-10 py-3 text-[15px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 max-sm:w-full"
                >
                  {signing ? 'Finishing…' : 'FINISH'}
                </button>
              </div>
            </div>
          </section>
        </div>
      </W2SelectionStepLayout>
    </>
  );
};
